import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Application from './pages/Application';
import MyApplication from './pages/MyApplication';
import PFS from './pages/PFS';
import DebtSchedule from './pages/DebtSchedule';
import Documents from './pages/Documents';
import Programs from './pages/Programs';
import NotFound from './pages/NotFound';
import AdminDashboard from './pages/admin/Dashboard';
import UWWorkbookPage from './pages/UWWorkbookPage';
import SalesDashboard from './pages/sales/SalesDashboard';
import FundedDashboard from './pages/funded/FundedDashboard';
import ClientsView from './pages/funded/ClientsView';
import ClientDetail from './pages/funded/ClientDetail';
import LedgerView from './pages/funded/LedgerView';
import UnmatchedView from './pages/funded/UnmatchedView';
import LendersView from './pages/funded/LendersView';
import LeadMatchesView from './pages/funded/LeadMatchesView';
import ChangePassword from './pages/ChangePassword';
import { CircularProgress, Box } from '@mui/material';
import './i18n/config'; // Initialize i18n

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const SalesRoute = ({ children }) => {
  const { isAuthenticated, isSales, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  return isSales ? children : <Navigate to="/dashboard" />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return isAdmin ? children : <Navigate to="/dashboard" />;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="application" element={<Application />} />
        <Route path="my-application" element={<MyApplication />} />
        <Route path="pfs" element={<PFS />} />
        <Route path="debt-schedule" element={<DebtSchedule />} />
        <Route path="documents" element={<Documents />} />
        <Route path="programs" element={<Programs />} />
        <Route path="change-password" element={<ChangePassword />} />
      </Route>

      <Route path="/admin" element={<AdminRoute><Layout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
      </Route>

      <Route path="/sales" element={<SalesRoute><Layout /></SalesRoute>}>
        <Route index element={<SalesDashboard />} />
        <Route path="matches" element={<LeadMatchesView />} />
        <Route path="funded" element={<FundedDashboard />} />
        <Route path="funded/clients" element={<ClientsView />} />
        <Route path="funded/clients/:clientId" element={<ClientDetail />} />
        <Route path="funded/ledger" element={<LedgerView />} />
        <Route path="funded/unmatched" element={<UnmatchedView />} />
        <Route path="funded/lenders" element={<LendersView />} />
      </Route>

      {/* UW Workbook — admin only, full screen, no Layout wrapper */}
      <Route path="/uw-workbook" element={<AdminRoute><UWWorkbookPage /></AdminRoute>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
